import { booleanAttribute, ChangeDetectionStrategy, Component, inject, Input, WritableSignal, signal } from '@angular/core'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import {
  EventTimePickerComponent,
  EVENT_TIME_PICKER_HOUR_OPTIONS,
  EVENT_TIME_PICKER_MINUTE_OPTIONS,
  snapMinuteToEventPickerGrid
} from '../../event-time-picker/event-time-picker.component'

export type TimeRange = { startTime: string; endTime: string }

const MIN_TIME_RANGE_MINUTES = 60

@Component({
  selector: 'app-time-range-picker-content',
  standalone: true,
  imports: [EventTimePickerComponent],
  templateUrl: './time-range-picker-content.component.html',
  styleUrl: './time-range-picker-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeRangePickerContentComponent {
  private readonly _alertService: AlertService = inject(AlertService)

  /** When true (e.g. shared `<modal>`), use modal shell styling without an outer border. */
  @Input({ transform: booleanAttribute }) public inModal: boolean = false

  public initialStartTime: string = '00:00'
  public initialEndTime: string = '00:00'
  public onApply: (range: TimeRange) => void = () => {}
  public closeDropdown: () => void = () => {}

  public readonly startHour: WritableSignal<string> = signal<string>('00')
  public readonly startMinute: WritableSignal<string> = signal<string>('00')
  public readonly endHour: WritableSignal<string> = signal<string>('00')
  public readonly endMinute: WritableSignal<string> = signal<string>('00')

  public readonly hourOptions: readonly string[] = EVENT_TIME_PICKER_HOUR_OPTIONS
  public readonly minuteOptions: readonly string[] = EVENT_TIME_PICKER_MINUTE_OPTIONS.filter((m: string) => m !== '59')

  /**
   * @description Initialize picker state from `initialStartTime` and `initialEndTime`.
   */
  public init(): void {
    this._applyInitialTimesToSignals()
  }

  /**
   * @description Sync internal signals to provided initial time strings.
   */
  private _applyInitialTimesToSignals(): void {
    const [startHour, startMinute]: [string | null, string | null] = this._parse(this.initialStartTime)
    const [endHour, endMinute]: [string | null, string | null] = this._parse(this.initialEndTime)
    if (startHour !== null) this.startHour.set(startHour)
    if (startMinute !== null) this.startMinute.set(startMinute)
    if (endHour !== null) this.endHour.set(endHour)
    if (endMinute !== null) this.endMinute.set(endMinute)
  }

  /**
   * @description Parse `HH:mm` into `[hour, minute]`, snapping minute to picker grid.
   * @param t - Raw time string.
   * @returns Tuple of hour/minute tokens or nulls when invalid.
   */
  private _parse(t: string): [string | null, string | null] {
    const m: RegExpExecArray | null = /^(\d{1,2}):(\d{2})$/.exec(t?.trim() || '')
    if (!m) return [null, null]
    const hNum: number = parseInt(m[1], 10)
    const minNum: number = parseInt(m[2], 10)
    if (hNum < 0 || hNum > 23 || minNum < 0 || minNum > 59) return [null, null]
    const min: string = snapMinuteToEventPickerGrid(minNum)
    return [String(hNum).padStart(2, '0'), min]
  }

  /**
   * @description Build a `HH:mm` string from hour and minute tokens.
   * @param hour - Two-digit hour.
   * @param minute - Two-digit minute.
   * @returns `HH:mm` time string.
   */
  public buildTime(hour: string, minute: string): string {
    return `${hour}:${minute}`
  }

  /**
   * @description Convert `HH:mm` string to minutes since midnight for comparisons.
   * @param time - Time string.
   * @returns Total minutes since 00:00.
   */
  private _toMinutes(time: string): number {
    const [h, m]: number[] = time.split(':').map((v: string) => parseInt(v, 10) || 0)
    return h * 60 + m
  }

  /**
   * @description Validate and emit selected time range, then close parent UI.
   */
  public apply(): void {
    const startTime: string = this.buildTime(this.startHour(), this.startMinute())
    const endTime: string = this.buildTime(this.endHour(), this.endMinute())

    const startMin: number = this._toMinutes(startTime)
    const endMin: number = this._toMinutes(endTime)

    if (startMin >= endMin) {
      this._alertService.alert(Alert.ERROR, 'Opening time must be before closing time.')
      return
    }

    if (endMin - startMin < MIN_TIME_RANGE_MINUTES) {
      this._alertService.alert(Alert.ERROR, 'Event timing must be at least 1 hour.')
      return
    }

    this.onApply({ startTime, endTime })
    this.closeDropdown()
  }

  /**
   * @description Restore fields from initial values without applying or closing.
   */
  public reset(): void {
    this._applyInitialTimesToSignals()
  }
}
